import { QRCodeSVG } from 'qrcode.react';
import type { ReactNode } from 'react';
import { Modal } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import type { ChannelDto, ChannelStatus } from './api';

/** Small lane-B2a UI helpers (kept inside the feature folder; not shared UI). */

export const STATUS_PILL: Record<ChannelStatus, ['ok' | 'warn' | 'danger' | 'info' | '', string]> = {
  ready: ['ok', 'מחובר'],
  connecting: ['info', 'מתחבר'],
  qr_required: ['warn', 'דורש QR'],
  created: ['warn', 'לא מקושר'],
  disconnected: ['danger', 'מנותק'],
  failed: ['danger', 'תקלה'],
  restricted: ['danger', 'מוגבל'],
};

export function ChannelStatusPill({ channel }: { channel: Pick<ChannelDto, 'status' | 'killSwitch'> }) {
  if (channel.killSwitch)
    return (
      <span className="pill warn">
        <span className="d" />
        עצירת חירום
      </span>
    );
  const [tone, label] = STATUS_PILL[channel.status] ?? ['', channel.status];
  return (
    <span className={`pill ${tone}`}>
      <span className="d" />
      {label}
    </span>
  );
}

/** The QR as the server sends it: a PNG data URL (OpenWA) — or a raw QR string rendered with QRCodeSVG. */
export function QrImage({ value }: { value: string }) {
  if (value.startsWith('data:image/')) return <img src={value} alt="קוד QR לקישור המספר" />;
  return <QRCodeSVG value={value} size={220} marginSize={0} fgColor="#2B1550" role="img" aria-label="קוד QR לקישור המספר" />;
}

/** Confirm modal with the mockup markup and a danger/primary action. */
export function ConfirmModal(props: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  disabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={props.open}
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button
            className={`btn ${props.tone === 'primary' ? 'primary' : 'danger solid'}`}
            disabled={props.busy || props.disabled}
            onClick={props.onConfirm}
          >
            {props.busy ? 'רגע…' : props.confirmLabel}
          </button>
          <button className="btn" onClick={props.onClose}>
            {props.cancelLabel ?? 'ביטול'}
          </button>
        </>
      }
    >
      {props.children}
    </Modal>
  );
}

/** Inline error for a panel whose query failed. */
export function InlineError({ text, onRetry }: { text: string; onRetry?: () => void }) {
  return (
    <div className="callout danger mb">
      <Icon name="alert" />
      <div>
        {text}{' '}
        {onRetry ? (
          <button className="btn sm ghost" onClick={onRetry}>
            <Icon name="refresh" />
            ניסיון נוסף
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Copy to clipboard; returns false when the browser blocks it. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
